import { Box, Typography } from '@mui/material';
import React, { forwardRef, useRef } from 'react';
import rpcShared from '@rockpapercoin/rpc-shared';
import styles from './InvoiceEditLineItemSummary.module.scss';
import {
	InvoiceEditLineItemDiscount,
	InvoiceEditLineItemDiscountProps,
} from './InvoiceEditLineItemDiscount';
import { gql, DocumentNode } from '@apollo/client';
import {
	InvoiceEditLineItemSummaryInvoiceFragment,
	InvoiceStatus,
} from '#types';
import classNames from 'classnames';

const invoiceEditLineItemSummaryInvoiceFragment = gql`
	fragment invoiceEditLineItemSummaryInvoice on Invoice {
		id
		status
		organization {
			country
		}
		...invoiceEditLineItemDiscountInvoice
		lineItems {
			id
			name
			quantity
			rate
			taxPercentage
		}
	}
	${ InvoiceEditLineItemDiscount.fragments
		.invoiceEditLineItemDiscountInvoiceFragment }
`;

export type InvoiceEditLineItemSummaryProps = {
	invoice?: Omit<InvoiceEditLineItemSummaryInvoiceFragment, 'id'> & {
		id?: string;
	};
	setInvoice: React.Dispatch<
		React.SetStateAction<
			Omit<InvoiceEditLineItemSummaryInvoiceFragment, 'id'> & { id?: string }
		>
	>;
	className?: string;
};

type InvoiceEditLineItemSummaryPropsWithFragment =
	React.FC<InvoiceEditLineItemSummaryProps> & {
		fragments: {
			invoiceEditLineItemSummaryInvoiceFragment: DocumentNode;
		};
	};
type SummaryItemProps = {
	label: string;
	value: string;
	className?: string;
	editComponent?: React.JSX.Element;
};

const SummaryItem = forwardRef<HTMLDivElement, SummaryItemProps>(
	( { label, value, className, editComponent }, ref ) => (
		<>
			<Typography
				ref={ ref }
				data-cy={ `line-item-summary-${ label
					.replace( /\s/, '-' )
					.toLowerCase() }-label` }
				className={ classNames( className, styles.label ) }
			>
				{ label }
				{ editComponent ? editComponent : null }
			</Typography>
			<Typography
				data-cy={ `line-item-summary-${ label
					.replace( /\s/, '-' )
					.toLowerCase() }-value` }
				className={ classNames( className, styles.value ) }
			>
				{ value }
			</Typography>
		</>
	)
);

export const InvoiceEditLineItemSummary: InvoiceEditLineItemSummaryPropsWithFragment =
	( { invoice, setInvoice, className } ) => {
		const ref = useRef<HTMLDivElement>( null );
		const discountPercentAsString = invoice?.discountIsPercentage
			? ` (${ invoice.discountPercentage }%)`
			: '';
		const discountDisabled =
			( invoice?.status &&
				[
					InvoiceStatus.Disputed,
					InvoiceStatus.Hidden,
					InvoiceStatus.Void,
				].includes( invoice.status ) ) ||
			false;

		const currencyLabel = rpcShared.invoices.getCurrencyLabel(
			invoice?.organization?.country
		);

		return (
			<Box
				data-cy='line-item-summary'
				className={ classNames( styles.container, className, {
					[ styles.discountDisabled ]: discountDisabled,
				} ) }
			>
				<SummaryItem
					label='Subtotal'
					value={ rpcShared.invoices.calculateInvoiceSubtotalToString(
						invoice?.lineItems
					) }
				/>
				<SummaryItem
					ref={ ref }
					label={ `Discount${ discountPercentAsString }` }
					value={
						invoice
							? rpcShared.invoices.calculateInvoiceDiscountToString( invoice )
							: '$0.00'
					}
					className={ !discountDisabled ? styles.discountEnabled : undefined }
					editComponent={
						discountDisabled ? undefined : (
							<InvoiceEditLineItemDiscount
								anchorEl={ ref.current }
								invoice={ invoice }
								setInvoice={
									setInvoice as InvoiceEditLineItemDiscountProps['setInvoice']
								}
								className={ styles.button }
							/>
						)
					}
				/>
				<SummaryItem
					label='Tax'
					value={
						( invoice
							? rpcShared.invoices.calculateInvoiceTaxTotalToString( invoice )
							: undefined ) || '$0.00'
					}
				/>
				<SummaryItem
					className={ styles.total }
					label={ `Invoice Total${ currencyLabel }` }
					value={
						invoice
							? rpcShared.invoices.calculateInvoiceTotalToString( invoice )
							: '$0.00'
					}
				/>
			</Box>
		);
	};

InvoiceEditLineItemSummary.fragments = {
	invoiceEditLineItemSummaryInvoiceFragment,
};
